<template>
    <section v-if="codeRefs.length || show">
        <b-field
            v-show="!listmode && codeRefs.length > 0"
            class="expand-to-container"
            :message="invalidSelection ? invalidSelectionMessage : message"
            :type="invalidSelection ? 'is-warning' : messageType">
            <b-input
                v-model="selectedDisplay"
                :placeholder="placeholder"
                :disabled="disabled || codeRefs.length === 0 || null"
                aria-haspopup="true"
                aria-controls="dropdown-menu"
                icon-right="chevron-down"
                @click.prevent="changeToListMode" />
        </b-field>
        <p
            v-if="invalidSelection && message"
            v-show="!listmode && codeRefs.length > 0"
            class="help"
            :class="messageType">
            {{ message }}
        </p>
        <div v-show="listmode || codeRefs.length === 0">
            <b-autocomplete
                ref="listRef"
                v-model="filter"
                :disabled="disabled || codeRefs.length === 0"
                clear-on-select
                :placeholder="placeholder"
                keep-first
                open-on-focus
                :loading="loading"
                :data="filteredRefs"
                :dropdown-position="position"
                aria-role="list"
                @blur="handleBlur"
                @focus="handleFocus"
                @select="handleSelect">
                <template #default="props">
                    <div
                        class="media"
                        :class="{ 'is-not-selectable': !isOptionSelectable(props.option) }">
                        <div
                            v-if="complex"
                            class="media-content">
                            {{ props.option.name }}
                            <small> - </small>{{ props.option.description }}
                            <b-tag
                                v-if="props.option.isNew"
                                class="level-right"
                                type="is-danger is-light">
                                not released
                            </b-tag>
                            <br>
                            <small>
                                Identity: <b>{{ props.option.identity }}</b>
                                , Is Valid: <bool-element :value="props.option.isValid" />
                            </small>
                            <b-tag
                                v-if="!isOptionSelectable(props.option) && props.option.unselectableReason"
                                class="ml-2"
                                type="is-warning is-light">
                                {{ props.option.unselectableReason }}
                            </b-tag>
                        </div>
                        <div
                            v-else
                            class="media-content">
                            <b>{{ props.option.name }}</b>
                            <small>
                                {{ showFullText(props.option) }}
                            </small>
                            <b-tag
                                v-if="!isOptionSelectable(props.option) && props.option.unselectableReason"
                                class="ml-2"
                                type="is-warning is-light">
                                {{ props.option.unselectableReason }}
                            </b-tag>
                        </div>
                    </div>
                </template>
                <template #empty>
                    <span class="empty-message">{{ emptyMessage || 'No matches' }}</span>
                </template>
            </b-autocomplete>
        </div>
    </section>
</template>

<script>

    import BoolElement from './BoolElement.vue';
    import { levenshtein } from '@/shared/helpers/utils';

    export default {
        components: {
            BoolElement
        },
        props: {
            show: {
                type: Boolean,
                required: false,
                default: false
            },
            complex: {
                type: Boolean,
                required: false,
                default: true
            },
            selected: {
                type: String,
                required: false,
                default: ''
            },
            codeRefs: {
                type: Array,
                required: true
            },
            loading: {
                type: Boolean,
                required: false,
                default: false
            },
            required: {
                type: Boolean,
                required: false,
                default: false
            },
            disabled: {
                type: Boolean,
                required: false,
                default: false
            },
            sortFunc: {
                type: Function,
                default: null,
                require: false
            },
            placeholderOverride: {
                type: String,
                default: null,
                require: false
            },
            position: {
                type: String,
                default: 'auto',
                require: false
            },
            message: {
                type: String,
                default: '',
                require: false
            },
            messageType: {
                type: String,
                default: '',
                require: false
            },
            invalidSelectionMessage: {
                type: String,
                default: '',
                require: false
            },
            // Shown in the open dropdown when it has no options to list, instead of the general 'No matches'
            emptyMessage: {
                type: String,
                default: '',
                require: false
            }
        },
        emits: [
            'blur',
            'focus',
            'ref-selected'
        ],
        data() {
            return {
                filter: '',
                selectedDisplay: '',
                select: null,
                listmode: false,
                invalidSelection: false
            };
        },
        computed: {
            filteredRefs() {
                const search = this.filter.toLowerCase();
                const self = this;

                return this.codeRefs
                    .filter(ref => !ref.isFiltered && !ref.isHidden && (ref.name.toLowerCase().includes(search) || ref.description?.toLowerCase().includes(search)))
                    .sort(function(a, b) {
                        if (self.sortFunc) {
                            return self.sortFunc(a, b);
                        }
                        if (search === '')
                            return a.name.localeCompare(b.name);

                        return levenshtein(a.name, search) - levenshtein(b.name, search);
                    })
                    .slice(0, 100);
            },
            placeholder() {
                if (this.placeholderOverride !== null)
                    return this.placeholderOverride;
                if (this.codeRefs.length > 100)
                    return 'Search for an item to select it';
                else
                    return 'Select an item';
            }
        },
        watch: {
            selected(id) {
                this.invalidSelection = false;
                if (!id || id === '') {
                    this.selectedDisplay = '';
                } else {
                    const sel = this.codeRefs.find(r => r.identity === id);
                    if (sel.isFiltered) {
                        this.invalidSelection = true;
                    }
                    this.handleSelectedText(sel);
                }
            }
        },
        mounted() {
            if (this.codeRefs.length && this.selected && this.selected.length) {
                const current = this.codeRefs.find(ref => ref.identity === this.selected);
                if (current.isFiltered) {
                    this.invalidSelection = true;
                }
                this.handleSelectedText(current);
            }
        },
        methods: {
            showFullText(option) {
                if (option.description?.length > 0) {
                    return `- ${option.description}`;
                }
            },
            handleBlur() {
                this.$emit('blur');
                const self = this;
                setTimeout(() => self.listmode = false, 300);
            },
            handleFocus() {
                this.$emit('focus');
            },
            handleSelect(option) {
                if (!option) {
                    return;
                }
                if (!this.isOptionSelectable(option)) {
                    this.filter = '';
                    this.select = null;
                    return;
                }
                this.handleSelectedText(option);
                this.filter = '';
                this.$emit('ref-selected', this.select);
            },
            isOptionSelectable(option) {
                return option?.isSelectable !== false;
            },
            handleSelectedText(obj) {
                this.listmode = false;
                this.select = obj.identity;

                this.selectedDisplay = obj.name + (obj.description ? ' - ' + obj.description : '');
                if (obj.isNew) {
                    this.selectedDisplay += '   (not released)';
                }
            },
            changeToListMode() {
                const self = this;
                if (!this.disabled) {
                    this.listmode = true;
                    setTimeout(() => self.$refs.listRef.focus(), 150);
                }
            }
        }
    };
</script>

<style scoped>
.align-icon-left {
    margin-right: 0 !important;
    margin-left: auto !important;
}

.expand-to-container {
    width: 100%;
}

.empty-message {
    white-space: normal;
}

.is-not-selectable {
    cursor: not-allowed;
    opacity: 0.65;
}
</style>
